import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'
import useItemPolicyEvaluation from 'businessTraveller/hooks/itemPolicy/useItemPolicyEvaluation'
import { generateItemPolicyOfferCreditKey } from 'businessTraveller/utils/generateItemPolicyKeys'
import { mergeItemPolicyEvaluationWithOfferCredit } from 'businessTraveller/utils/mapItemPolicyRowsToOfferCredit'
import { selectItemPolicyOfferCredit } from 'businessTraveller/selectors/itemPolicy/selectItemPolicyOfferCredit'
import { selectSelectedTravellerEmployeeIds } from 'businessTraveller/selectors/itemPolicy/selectSelectedTravellerEmployeeIds'
import { getLocationDataFromOffer } from 'businessTraveller/utils/getLocationDataFromOffer'
import {
  hasHotelItemPolicyLocation,
  resolveHotelItemPolicyLocation,
} from 'businessTraveller/utils/resolveHotelItemPolicyLocation'
import GeoContext from 'contexts/geoContext'
import { usePlaceByCoords } from 'hooks/usePlace'
import useQueryParams from 'hooks/useQueryParams'
import { getCenterCoordinates } from 'lib/search/searchUtils'
import { getSearchItemFromURLSearchParams } from 'lib/url/searchUrlUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useContext, useEffect, useMemo } from 'react'

interface Params {
  cost: number
  numberOfNights: number
  offer: App.AnyOffer
  roomCount?: number
}

function useBusinessTravellerItemPolicyOfferCreditFetcher(params: Params) {
  const {
    cost,
    numberOfNights,
    offer,
    roomCount = 1,
  } = params
  const { currentCurrency: currencyCode } = useContext(GeoContext)
  const appDispatch = useAppDispatch()
  const queryParams = useQueryParams()
  const employeeIds = useAppSelector(selectSelectedTravellerEmployeeIds)
  const businessId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.business?.id)

  const searchItem = useMemo(() => getSearchItemFromURLSearchParams(queryParams), [queryParams])
  const mapViewCoords = useMemo(() => {
    if (searchItem?.searchType === 'bounds') {
      const coords = searchItem.value?.split(',')
      if (coords && coords.length === 4) {
        const numCoords = coords.map(parseFloat)
        return getCenterCoordinates(numCoords as [number, number, number, number])
      }
    }
    return []
  }, [searchItem])
  const [place] = usePlaceByCoords(mapViewCoords[0], mapViewCoords[1])

  const location = useMemo(() => {
    const offerLocation = getLocationDataFromOffer(offer).location
    let resolvedLatitude = searchItem?.searchType === 'lat_long' ? searchItem.lat?.toString() : undefined
    let resolvedLongitude = searchItem?.searchType === 'lat_long' ? searchItem.lng?.toString() : undefined

    if (mapViewCoords.length >= 2) {
      resolvedLatitude = mapViewCoords[0].toString()
      resolvedLongitude = mapViewCoords[1].toString()
    }

    return resolveHotelItemPolicyLocation({
      latitude: offerLocation.latitude || resolvedLatitude,
      longitude: offerLocation.longitude || resolvedLongitude,
      placeFromId: place,
    }) ?? { latitude: '', longitude: '' }
  }, [mapViewCoords, offer, place, searchItem])

  const hasValidLocation = hasHotelItemPolicyLocation(location)

  const { cacheKey, item } = useMemo(() => {
    const key = `hotel_${offer.id}_${cost}_${numberOfNights}_${roomCount}`
    const hotelItem: App.BusinessTraveller.ItemPolicyEvaluationHotelItem = {
      itemPolicyType: 'HOTEL',
      key,
      cost,
      currencyCode,
      numberOfNights,
      roomCount,
      travellerCount: 1,
      location,
    }
    return {
      cacheKey: generateItemPolicyOfferCreditKey(hotelItem, offer.id, employeeIds),
      item: hotelItem,
    }
  }, [cost, currencyCode, employeeIds, location, numberOfNights, offer, roomCount])

  useEffect(() => {
    appDispatch(fetchAccountBusiness())
  }, [appDispatch])

  const mappedOfferCredit = useAppSelector(state => selectItemPolicyOfferCredit(state, cacheKey))
  const itemPolicyData = useItemPolicyEvaluation({ enabled: hasValidLocation, businessId, cacheKey, item, employeeIds })
  const offerCreditData = mergeItemPolicyEvaluationWithOfferCredit(mappedOfferCredit, itemPolicyData)

  return {
    offerCredit: mappedOfferCredit,
    offerCreditData,
    cacheKey,
    item,
    itemPolicyData,
    employeeIds,
  }
}

export default useBusinessTravellerItemPolicyOfferCreditFetcher
